<template>
  <ib-application setupSSO>
    <ElectionHub
      v-if="entryReady"
      :betslip-visible="shouldOpenBetSlip"
      :betslip-config="betSlipConfig"
      :share-dialog-visible="shouldOpenShareDialog"
      :share-dialog-config="shareDialogConfig"
    />
    <div v-else class="min-h-screen bg-surface" aria-hidden="true" />
  </ib-application>
</template>

<script setup lang="ts">
import { inject, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { app } from '@ibgroup-tws-webapps/ratio.ui';
import { usePositionsManager } from '@/composables/usePositionsManager';
import { useUserState } from '@/stores/useUserState';
import { useModuleDeepLink } from '@/composables/useModuleDeepLink';
import { useCommonNativeActions } from '@/composables/useCommonNativeActions';
import ElectionHub from './components/ElectionHub.vue';

const moduleProps = inject<{
  authenticated: boolean;
  accountId?: string;
  view?: string;
  conid?: string | number;
  productConid?: string | number;
  'product-conid'?: string | number;
  query?: Record<string, string | number | undefined>;
}>('moduleProps', {
  authenticated: false,
  accountId: '',
});

const userStateStore = useUserState();
const route = useRoute();
const router = useRouter();
const entryReady = ref(false);

function electionHubHashPath(): string | null {
  const path = window.location.hash.slice(1);
  return path.startsWith('/election-hub') ? path : null;
}

async function prepareElectionHubEntry() {
  entryReady.value = false;

  const moduleQuery = moduleProps.query;
  if (moduleQuery?.eb_view === 'grid' || moduleQuery?.eb_view === 'map') {
    const query = { ...moduleQuery };
    const name = query.eb_view === 'grid' ? 'ElectionGrid' : 'ElectionMap';
    delete query.eb_view;
    await router.replace({ name, query });
  } else {
    const hashPath = electionHubHashPath();
    if (hashPath && hashPath !== route.fullPath) await router.replace(hashPath);
  }

  await router.isReady();
  entryReady.value = true;
}

function handleHashChange() {
  const hashPath = electionHubHashPath();
  if (hashPath && hashPath !== route.fullPath) void prepareElectionHubEntry();
}

const { shouldOpenBetSlip, betSlipConfig, shouldOpenShareDialog, shareDialogConfig } = useModuleDeepLink(moduleProps);

watchEffect(() => {
  userStateStore.setUserState(moduleProps.authenticated, moduleProps.accountId ?? '');
});

watch(() => moduleProps.query, prepareElectionHubEntry, { deep: true });

onMounted(() => {
  window.addEventListener('hashchange', handleHashChange);
  void prepareElectionHubEntry();
});

onBeforeUnmount(() => {
  window.removeEventListener('hashchange', handleHashChange);
});

useCommonNativeActions();
usePositionsManager(() => moduleProps.accountId);

watch(
  [() => app.option.platform, () => app.option.clientPlatform],
  ([platform, clientPlatform]) => {
    if (platform === 'desktop' || clientPlatform === 'desktop') {
      sendToWebApp({ action: 'options', data: { clientPlatform: 'web' } });
    }
  },
  { immediate: true },
);
</script>
